import React, { useEffect, useRef, useState } from "react";
import { Trash2 } from "lucide-react";
import { Button } from "../../ui";
import { VoicePreviewPlayer } from "./VoicePreviewPlayer";

interface VoiceRecorderProps {
  /** called with the recorded clip + its duration when the user hits send */
  onSend: (audio: Blob, durationMs: number) => void;
  /** called when the user backs out without sending */
  onCancel: () => void;
  /** parent is uploading/transcribing — disables send + shows a spinner */
  sending?: boolean;
  /** label for the confirm button (default "send"; e.g. "use recording" in the coffee form) */
  sendLabel?: string;
  /** buzz on iOS; only where the recorder sits in a composer that never scrolls (see Button) */
  iosTapOverlay?: boolean;
}

// cap a single note so files stay small and transcription stays cheap.
const MAX_MS = 120_000;

function pickMime(): string {
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
  for (const c of candidates) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.(c)) return c;
  }
  return "";
}

function fmt(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function VoiceRecorder({ onSend, onCancel, sending, sendLabel = "send", iosTapOverlay }: VoiceRecorderProps) {
  const [phase, setPhase] = useState<"requesting" | "recording" | "preview" | "error">("requesting");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startRef = useRef(0);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const durationRef = useRef(0);
  const previewUrlRef = useRef<string | null>(null);

  const stopTicker = () => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  };
  const cleanupStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const mime = pickMime();
        const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
        recorderRef.current = rec;
        chunksRef.current = [];
        rec.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
        };
        rec.onstop = () => {
          durationRef.current = Date.now() - startRef.current;
          const blob = new Blob(chunksRef.current, { type: rec.mimeType || mime || "audio/webm" });
          blobRef.current = blob;
          const url = URL.createObjectURL(blob);
          previewUrlRef.current = url;
          setPreviewUrl(url);
          setPhase("preview");
          stopTicker();
          cleanupStream();
        };
        startRef.current = Date.now();
        rec.start();
        setPhase("recording");
        tickRef.current = setInterval(() => {
          const e = Date.now() - startRef.current;
          setElapsedMs(e);
          if (e >= MAX_MS && rec.state !== "inactive") rec.stop();
        }, 200);
      } catch {
        setErrorMsg("couldn't access the microphone — check browser permissions");
        setPhase("error");
        cleanupStream();
      }
    })();
    return () => {
      cancelled = true;
      stopTicker();
      try {
        if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
      } catch {
        /* ignore */
      }
      cleanupStream();
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopRecording = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
  };

  const handleSend = () => {
    if (blobRef.current && blobRef.current.size > 0) onSend(blobRef.current, durationRef.current);
  };

  if (phase === "error") {
    return (
      <div className="flex-1 flex items-center justify-between gap-2 text-xs text-red-600">
        <span className="truncate">{errorMsg}</span>
        <Button type="button" size="sm" iosTapOverlay={iosTapOverlay} variant="ghost" onClick={onCancel}>
          back
        </Button>
      </div>
    );
  }

  if (phase === "preview") {
    return (
      <div className="flex-1 flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={sending}
          className="shrink-0 text-gray-400 hover:text-red-600 disabled:opacity-50 flex items-center"
          aria-label="discard recording"
          title="discard"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        {previewUrl && (
          <VoicePreviewPlayer src={previewUrl} durationMs={durationRef.current} className="flex-1" />
        )}
        <Button type="button" size="sm" iosTapOverlay={iosTapOverlay} onClick={handleSend} loading={sending} disabled={sending} className="shrink-0">
          {sendLabel}
        </Button>
      </div>
    );
  }

  // requesting / recording
  return (
    <div className="flex-1 flex items-center gap-2">
      <span className="shrink-0 flex items-center gap-1.5 text-xs text-gray-600">
        <span
          className={`inline-block w-2.5 h-2.5 rounded-full ${phase === "recording" ? "bg-red-500 animate-pulse" : "bg-gray-300"}`}
        />
        {phase === "recording" ? fmt(elapsedMs) : "starting…"}
      </span>
      <span className="flex-1 text-[10px] text-gray-400 truncate">recording — tap stop when done</span>
      <Button type="button" size="sm" iosTapOverlay={iosTapOverlay} variant="ghost" onClick={onCancel} className="shrink-0">
        cancel
      </Button>
      <Button type="button" size="sm" iosTapOverlay={iosTapOverlay} onClick={stopRecording} disabled={phase !== "recording"} className="shrink-0">
        stop
      </Button>
    </div>
  );
}
